import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@akostm/orbit-ui';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';

/**
 * Generates unique IDs for DOM elements and ARIA relationships.
 * Uses crypto.randomUUID() when available (cryptographically secure).
 * Falls back to Math.random() which is safe here as IDs are only used for:
 * - HTML id attributes
 * - ARIA labelledby/describedby relationships
 * - Not for security, authentication, or cryptographic purposes
 */
const uniqueId = () => globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);

@Component({
  selector: 'shared-global-error',
  standalone: true,
  imports: [TranslatePipe, ButtonComponent],
  templateUrl: './global-error.component.html',
  styleUrl: './global-error.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalErrorComponent implements AfterViewInit, OnDestroy {
  readonly title = input<string>();
  readonly message = input<string>('SHARED.ERRORS.GLOBAL.MESSAGE');
  readonly hasReload = input<boolean>(true);
  readonly unknownError = input<boolean>(true);
  readonly computedTitle = computed(
    () =>
      this.title() ||
      (this.unknownError()
        ? 'SHARED.ERRORS.GLOBAL.UNKNOWN_ERROR_TITLE'
        : 'SHARED.ERRORS.GLOBAL.TITLE')
  );
  readonly reload = output<void>();
  readonly testId = input<string>();
  readonly globalErrorType = input<'conversation-not-found' | 'general-error'>('general-error');
  readonly refreshIcon = faArrowsRotate;
  readonly titleId = `global-error-title-${uniqueId()}`;
  readonly messageId = `global-error-message-${uniqueId()}`;
  readonly dialogContentRef = viewChild<ElementRef<HTMLElement>>('dialogContent');
  readonly #focusTrapFactory = inject(FocusTrapFactory);
  #focusTrap?: FocusTrap;

  onReloadKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.reload.emit();
    }
  }

  ngAfterViewInit(): void {
    const dialogElement = this.dialogContentRef()?.nativeElement;
    if (!dialogElement) {
      return;
    }
    this.#focusTrap = this.#focusTrapFactory.create(dialogElement);
    setTimeout(() => dialogElement.focus(), 0);
  }

  ngOnDestroy(): void {
    this.#focusTrap?.destroy();
  }
}
